import { ChevronsUpDown, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { CurrencyInput } from "@/components/shared/currency-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { currentFiscalYear, formatFY } from "@/lib/fiscal";
import { cn } from "@/lib/utils";
import { CopyToFYSelect, SAVED_BORDER, SectionHeader, useSaveFlash } from "./shared";

export function AffiliateMappingSection({
  ownerId,
  fiscalYear,
  onDataChange,
  onRemove,
  readOnly = false,
}: {
  ownerId: string;
  fiscalYear: number;
  onDataChange: (hasData: boolean) => void;
  onRemove?: () => void;
  readOnly?: boolean;
}) {
  const currentFY = currentFiscalYear();
  const [mappings, setMappings] = useState<string[]>([]);
  const [allAffiliates, setAllAffiliates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { savedField: savedFlashId, flashSaved } = useSaveFlash();
  const [comboboxOpen, setComboboxOpen] = useState(false);
  // Typed text in the picker — lets someone add an affiliate who has no deals yet.
  const [search, setSearch] = useState("");
  const [bonusPerDeal, setBonusPerDeal] = useState<string | number>("");
  const [drBonusPerDeal, setDrBonusPerDeal] = useState<string | number>("");
  const [hasDirectReports, setHasDirectReports] = useState(false);
  const lastSavedBonus = useRef<string | number>("");
  const lastSavedDrBonus = useRef<string | number>("");
  const hasBonusConfig = useRef(false);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/affiliate-mappings?fy=${fiscalYear}`);
      if (res.ok) {
        const data = await res.json();
        setMappings(data.mappings);
        onDataChange(data.mappings.length > 0 || hasBonusConfig.current);
      }
    } finally {
      setLoading(false);
    }
  }, [ownerId, fiscalYear, onDataChange]);

  const fetchOwnerData = useCallback(async () => {
    const res = await fetch(`/api/owners/${ownerId}`);
    if (res.ok) {
      const data = await res.json();
      const val = data.owner?.affiliateBonusPerDeal ?? "";
      const drVal = data.owner?.drAffiliateBonusPerDeal ?? "";
      setBonusPerDeal(val);
      setDrBonusPerDeal(drVal);
      lastSavedBonus.current = val;
      lastSavedDrBonus.current = drVal;
      setHasDirectReports(data.owner?.hasDirectReports ?? false);
      // Keep section visible if bonus config exists, even with no affiliates
      hasBonusConfig.current = (Number(val) || 0) > 0 || (Number(drVal) || 0) > 0;
      if (hasBonusConfig.current) {
        onDataChange(true);
      }
    }
  }, [ownerId, onDataChange]);

  const fetchAllAffiliates = useCallback(async () => {
    const res = await fetch("/api/deals/affiliate-names");
    if (res.ok) {
      const data = await res.json();
      setAllAffiliates(data.values ?? []);
    }
  }, []);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  useEffect(() => {
    fetchOwnerData();
  }, [fetchOwnerData]);

  useEffect(() => {
    fetchAllAffiliates();
  }, [fetchAllAffiliates]);

  const mappedSet = new Set(mappings);
  const availableToAdd = allAffiliates.filter((a) => !mappedSet.has(a));
  // A typed name that isn't on any deal yet (and isn't already mapped) can still be
  // added; its bonus starts counting once HubSpot deals carry that exact name.
  const typedName = search.trim().replace(/\s+/g, " ");
  const typedIsNew =
    typedName.length > 0 &&
    ![...allAffiliates, ...mappings].some((a) => a.toLowerCase() === typedName.toLowerCase());

  async function syncMappings(updatedList: string[], changedName: string) {
    const res = await fetch(`/api/owners/${ownerId}/affiliate-mappings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear, mappings: updatedList }),
    });
    if (res.ok) {
      const data = await res.json();
      setMappings(data.mappings);
      onDataChange(data.mappings.length > 0);
      flashSaved(changedName);
    }
  }

  async function saveOwnerField(
    field: string,
    current: string | number,
    lastSaved: React.RefObject<string | number>,
    flashKey: string,
  ) {
    if (String(current) === String(lastSaved.current)) return;
    const value = current === "" ? null : Number(current);
    const res = await fetch(`/api/owners/${ownerId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [field]: value }),
    });
    if (res.ok) {
      lastSaved.current = current;
      flashSaved(flashKey);
    }
  }

  const saveBonus = () =>
    saveOwnerField("affiliateBonusPerDeal", bonusPerDeal, lastSavedBonus, "bonusPerDeal");
  const saveDrBonus = () =>
    saveOwnerField("drAffiliateBonusPerDeal", drBonusPerDeal, lastSavedDrBonus, "drBonusPerDeal");

  function addAffiliate(name: string) {
    syncMappings([...mappings, name], name);
  }

  function removeAffiliate(name: string) {
    syncMappings(
      mappings.filter((m) => m !== name),
      name,
    );
  }

  async function removeAll() {
    await Promise.all([
      fetch(`/api/owners/${ownerId}/affiliate-mappings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fiscalYear, mappings: [] }),
      }),
      fetch(`/api/owners/${ownerId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ affiliateBonusPerDeal: null, drAffiliateBonusPerDeal: null }),
      }),
    ]);
    setMappings([]);
    setBonusPerDeal("");
    setDrBonusPerDeal("");
    lastSavedBonus.current = "";
    lastSavedDrBonus.current = "";
    hasBonusConfig.current = false;
    onDataChange(false);
    onRemove?.();
  }

  async function copyToFY(targetFY: number) {
    if (mappings.length === 0) return;
    await fetch(`/api/owners/${ownerId}/affiliate-mappings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear: targetFY, mappings }),
    });
  }

  return (
    <Card>
      <CardContent className="pt-5">
        <SectionHeader title="Affiliate Coaching" onRemove={readOnly ? undefined : removeAll}>
          {!readOnly && mappings.length > 0 && (
            <CopyToFYSelect currentFY={currentFY} selectedFY={fiscalYear} onCopy={copyToFY} />
          )}
        </SectionHeader>

        <p className="text-xs text-muted-foreground mb-3">
          Every closed deal carrying a coached affiliate's name counts toward this person's
          affiliate bonus — whoever the consultant on the deal is.
        </p>

        <div
          className={cn(
            "mb-4 grid gap-4",
            hasDirectReports ? "grid-cols-2 max-w-[420px]" : "grid-cols-1 max-w-[200px]",
          )}
        >
          <div>
            <Label htmlFor="affiliateBonusPerDeal">Bonus/Deal</Label>
            <CurrencyInput
              id="affiliateBonusPerDeal"
              value={bonusPerDeal}
              onChange={setBonusPerDeal}
              onBlur={saveBonus}
              placeholder="e.g. $50"
              className={cn(
                savedFlashId === "bonusPerDeal" && SAVED_BORDER,
                "transition-[border-color,box-shadow] duration-300",
              )}
              disabled={readOnly}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {hasDirectReports ? "Own affiliate deals" : "Flat bonus per closed affiliate deal"}
            </p>
          </div>
          {hasDirectReports && (
            <div>
              <Label htmlFor="drAffiliateBonusPerDeal">DR Bonus/Deal</Label>
              <CurrencyInput
                id="drAffiliateBonusPerDeal"
                value={drBonusPerDeal}
                onChange={setDrBonusPerDeal}
                onBlur={saveDrBonus}
                placeholder="e.g. $25"
                className={cn(
                  savedFlashId === "drBonusPerDeal" && SAVED_BORDER,
                  "transition-[border-color,box-shadow] duration-300",
                )}
                disabled={readOnly}
              />
              <p className="text-xs text-muted-foreground mt-1">Direct reports' affiliate deals</p>
            </div>
          )}
        </div>

        <div className={loading ? "opacity-60" : ""}>
          {mappings.length === 0 && !loading ? (
            <p className="text-sm text-muted-foreground py-2">
              No affiliates mapped for {formatFY(fiscalYear)}.{!readOnly && " Add one below."}
            </p>
          ) : (
            <div className="space-y-2">
              {mappings.map((name) => (
                <div
                  key={name}
                  className={cn(
                    "flex items-center justify-between rounded-md border px-3 py-2 transition-[border-color,box-shadow] duration-300",
                    savedFlashId === name && SAVED_BORDER,
                  )}
                >
                  <span className="text-sm font-medium">{name}</span>
                  {!readOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeAffiliate(name)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!readOnly && (
            <div className="mt-4">
              <Popover
                open={comboboxOpen}
                onOpenChange={(open) => {
                  setComboboxOpen(open);
                  if (!open) setSearch("");
                }}
              >
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[250px] h-9 justify-between text-sm">
                    <span className="flex items-center gap-1.5">
                      <Plus className="h-3.5 w-3.5" />
                      Add Affiliate
                    </span>
                    <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search or type a new name..."
                      value={search}
                      onValueChange={setSearch}
                    />
                    <CommandList>
                      <CommandEmpty>No affiliate found.</CommandEmpty>
                      {availableToAdd.map((name) => (
                        <CommandItem
                          key={name}
                          value={name}
                          onSelect={() => {
                            addAffiliate(name);
                            setComboboxOpen(false);
                          }}
                        >
                          {name}
                        </CommandItem>
                      ))}
                      {typedIsNew && (
                        <CommandItem
                          key="__new__"
                          value={`add new ${typedName}`}
                          onSelect={() => {
                            addAffiliate(typedName);
                            setComboboxOpen(false);
                            setSearch("");
                          }}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add “{typedName}”{" "}
                          <span className="text-muted-foreground">(no deals yet)</span>
                        </CommandItem>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
